import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useI18n } from '../context/I18nContext';
import { ArrowLeft, Check, X, Loader2, ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { toast } from 'sonner';

export default function WatchDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [watch, setWatch] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On-request inline form
  const [req, setReq] = useState({ email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  // Fullscreen lightbox state
  const [lightbox, setLightbox] = useState(false);

  // Touch swipe state for mobile gallery navigation
  const [touchStartX, setTouchStartX] = useState(null);
  const SWIPE_THRESHOLD = 50; // px

  const onTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX == null || !watch?.images || watch.images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      const n = watch.images.length;
      if (dx < 0) setActiveImg((i) => (i + 1) % n);
      else setActiveImg((i) => (i - 1 + n) % n);
    }
    setTouchStartX(null);
  };

  // Keyboard navigation in lightbox + body scroll lock (iOS-safe)
  useEffect(() => {
    if (!lightbox) return;
    // iOS-safe scroll lock: freeze body in place and restore scroll position on close
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    const onKey = (e) => {
      if (!watch?.images || watch.images.length < 1) return;
      const n = watch.images.length;
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') setActiveImg((i) => (i + 1) % n);
      if (e.key === 'ArrowLeft') setActiveImg((i) => (i - 1 + n) % n);
    };
    // Block any touchmove on the background so the page underneath cannot scroll/zoom
    const blockBg = (e) => { e.preventDefault(); };

    window.addEventListener('keydown', onKey);
    document.addEventListener('touchmove', blockBg, { passive: false });
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('touchmove', blockBg);
    };
  }, [lightbox, watch]);

  useEffect(() => {
    api.get(`/watches/${id}`)
      .then((r) => setWatch(r.data))
      .catch(() => navigate('/catalog'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Preload every gallery image so switching between them is instant (no flicker / re-decode lag)
  useEffect(() => {
    if (!watch?.images) return;
    watch.images.forEach((src) => { const img = new Image(); img.src = src; });
  }, [watch]);

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', {
        ...req,
        name: req.email.split('@')[0] || 'Cliente',
        subject: `Request: ${watch.brand} ${watch.model} (${watch.reference || ''})`,
      });
      toast.success(t('checkout.request_success'));
      setReq({ email: '', phone: '', message: '' });
    } catch { toast.error('Error'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="max-w-[1440px] mx-auto px-6 py-32 text-center text-[#c9a961] font-serif text-2xl">Loading...</div>;
  if (!watch) return null;

  const localizedDesc = watch[`description_${lang}`] || watch.description;
  const onRequest = watch.availability === 'on_request';

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-10 lg:py-12" data-testid="watch-detail-page">
      <Link to="/catalog" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#c9a961] hover:text-[#d8c180] mb-10" data-testid="back-to-catalog">
        <ArrowLeft size={14} /> {t('catalog.title')}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* IMAGES */}
        <div className="lg:col-span-7 min-w-0 max-w-full">
          {/* Main image: ALWAYS a fixed square frame on every viewport,
              same shape & size logic so the controls never move. The image
              fits inside via object-contain. */}
          <div
            className="relative mx-auto border border-[#c9a961]/20 mb-4 overflow-hidden select-none bg-[#05150f] w-full aspect-square"
            style={{ touchAction: 'pan-y', maxWidth: '520px' }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {watch.images && watch.images[activeImg] ? (
              <img
                src={watch.images[activeImg]}
                alt=""
                onClick={() => setLightbox(true)}
                className="cursor-zoom-in w-full h-full object-contain"
                data-testid="main-watch-image"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#c9a961]/40 font-serif text-6xl">GN</div>
            )}

            {/* Fullscreen icon (top-right) */}
            {watch.images && watch.images[activeImg] && (
              <button
                type="button"
                onClick={() => setLightbox(true)}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors"
                style={{ touchAction: 'manipulation' }}
                aria-label="View fullscreen"
                data-testid="img-fullscreen"
              >
                <Expand size={16} />
              </button>
            )}

            {/* Prev / Next arrows */}
            {watch.images && watch.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveImg((i) => (i - 1 + watch.images.length) % watch.images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors"
                  style={{ touchAction: 'manipulation' }}
                  aria-label="Previous"
                  data-testid="img-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveImg((i) => (i + 1) % watch.images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors"
                  style={{ touchAction: 'manipulation' }}
                  aria-label="Next"
                  data-testid="img-next"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-3 right-3 text-xs tracking-[0.18em] text-[#c9a961] bg-[#05150f]/80 px-3 py-1 border border-[#c9a961]/30">
                  {activeImg + 1} / {watch.images.length}
                </div>
              </>
            )}
          </div>

          {/* Thumbnails — horizontal scroll strip, kept small */}
          {watch.images && watch.images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
              {watch.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`flex-shrink-0 w-16 h-16 overflow-hidden border-2 transition-colors ${i === activeImg ? 'border-[#c9a961]' : 'border-[#c9a961]/20 hover:border-[#c9a961]/50'}`}
                  style={{ touchAction: 'manipulation' }}
                  data-testid={`thumb-${i}`}
                  aria-label={`Image ${i + 1}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Description — desktop only (under gallery). On mobile, description is shown below the watch name in the info column. */}
          {localizedDesc && (
            <div className="hidden lg:block mt-10" data-testid="detail-description">
              <p className="gn-overline mb-4">{t('watch.description')}</p>
              <p className="text-base text-[#a3b5ad] font-light leading-relaxed">{localizedDesc}</p>
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <p className="gn-overline mb-3" data-testid="detail-brand">{watch.brand}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#f5f5f0] mb-3 leading-tight" data-testid="detail-model">{watch.model}</h1>
          <p className="text-sm text-[#a3b5ad] tracking-wider mb-8">{watch.reference}{watch.year ? ` · ${watch.year}` : ''}</p>

          {/* Description — mobile only, right under the watch name */}
          {localizedDesc && (
            <div className="lg:hidden mb-8" data-testid="detail-description-mobile">
              <p className="gn-overline mb-3">{t('watch.description')}</p>
              <p className="text-base text-[#a3b5ad] font-light leading-relaxed">{localizedDesc}</p>
            </div>
          )}

          <div className="border-t border-b border-[#c9a961]/15 py-8 mb-8">
            {onRequest ? (
              <div data-testid="detail-price-on-request">
                <p className="font-serif italic text-3xl text-[#c9a961] mb-3">{t('watch.price_on_request')}</p>
                <p className="text-sm text-[#a3b5ad] font-light leading-relaxed">{t('watch.on_request_note')}</p>
              </div>
            ) : (
              <p className="font-serif text-5xl text-[#c9a961]" data-testid="detail-price">
                {new Intl.NumberFormat('it-CH', { style: 'currency', currency: watch.currency || 'EUR', maximumFractionDigits: 0 }).format(watch.price)}
              </p>
            )}
          </div>

          {watch.sold ? (
            <div className="border border-[#c9a961]/40 px-6 py-4 mb-6 text-center text-[#c9a961] tracking-[0.2em] uppercase text-sm" data-testid="sold-banner">
              {t('watch.sold')}
            </div>
          ) : (
            // Pagamento immediato temporaneamente disattivato — sempre form richiesta info
            <form onSubmit={submitRequest} className="space-y-5 mb-10" data-testid="on-request-form">
              <p className="gn-overline mb-3">{t('checkout.request_t')}</p>
              <input required type="email" className="gn-input" placeholder={t('contact.email')} value={req.email} onChange={(e) => setReq({...req, email: e.target.value})} data-testid="req-email" />
              <input required className="gn-input" placeholder={t('contact.phone')} value={req.phone} onChange={(e) => setReq({...req, phone: e.target.value})} data-testid="req-phone" />
              <textarea className="gn-textarea" placeholder={t('contact.message')} value={req.message} onChange={(e) => setReq({...req, message: e.target.value})} data-testid="req-message" />
              <button type="submit" disabled={sending} className="gn-btn-primary w-full" data-testid="req-submit">
                {sending && <Loader2 className="animate-spin" size={16} />}
                {t('checkout.request_send')}
              </button>
            </form>
          )}

          {/* Specs */}
          <div>
            <p className="gn-overline mb-5">{t('watch.specs')}</p>
            <dl className="space-y-3 text-sm">
              {[
                ['reference', watch.reference],
                ['year', watch.year],
                ['condition', t(`catalog.condition_${watch.condition}`) !== `catalog.condition_${watch.condition}` ? t(`catalog.condition_${watch.condition}`) : watch.condition],
                ['movement', watch.movement],
                ['case_material', watch.case_material],
                ['case_size', watch.case_size],
                ['dial', watch.dial_color],
              ].filter(([_, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[#c9a961]/10 pb-3">
                  <dt className="text-[#a3b5ad]">{t(`watch.${k}`)}</dt><dd className="text-[#f5f5f0] font-medium">{v}</dd>
                </div>
              ))}
              <div className="flex justify-between border-b border-[#c9a961]/10 pb-3">
                <dt className="text-[#a3b5ad]">{t('watch.box')}</dt>
                <dd className="text-[#f5f5f0]">{watch.box ? <Check size={16} className="text-[#c9a961]" /> : <X size={16} className="text-[#a3b5ad]" />}</dd>
              </div>
              <div className="flex justify-between border-b border-[#c9a961]/10 pb-3">
                <dt className="text-[#a3b5ad]">{t('watch.papers')}</dt>
                <dd className="text-[#f5f5f0]">{watch.papers ? <Check size={16} className="text-[#c9a961]" /> : <X size={16} className="text-[#a3b5ad]" />}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* ─────────── Fullscreen Lightbox ─────────── */}
      {lightbox && watch.images && watch.images[activeImg] && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center select-none bg-[#05150f]"
          onClick={() => setLightbox(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="dialog"
          aria-modal="true"
          data-testid="lightbox"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors z-10"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>

          {/* Counter */}
          {watch.images.length > 1 && (
            <div className="absolute top-5 left-5 text-xs tracking-[0.2em] text-[#c9a961] bg-[#05150f]/80 px-3 py-2 border border-[#c9a961]/30 z-10">
              {activeImg + 1} / {watch.images.length}
            </div>
          )}

          {/* Prev / Next arrows */}
          {watch.images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + watch.images.length) % watch.images.length); }}
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors z-10"
                style={{ touchAction: 'manipulation' }}
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % watch.images.length); }}
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#05150f]/80 border border-[#c9a961]/40 text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors z-10"
                style={{ touchAction: 'manipulation' }}
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* The image — sits inside a fixed-aspect square box so the box never resizes */}
          <div
            className="relative aspect-square overflow-hidden flex items-center justify-center bg-transparent"
            style={{ width: 'min(78vw, 78vh)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={watch.images[activeImg]}
              alt=""
              className="w-full h-full object-contain"
              data-testid="lightbox-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}
